import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { Plus, Pencil, Trash2, UserCog, Shield, User, Building2, CheckCircle, XCircle } from 'lucide-react';

import { BACKEND_URL, API } from '../utils/apiConfig';
const VENUES = ['MAKS Pickleball - ISF', 'Sports Complex', 'Bhayandar'];
const ROLES = [
  { value: 'admin', label: 'Administrator', icon: Shield, color: 'bg-red-100 text-red-800' },
  { value: 'manager', label: 'Manager', icon: UserCog, color: 'bg-blue-100 text-blue-800' },
  { value: 'staff', label: 'Staff', icon: UserCog, color: 'bg-amber-100 text-amber-800' },
  { value: 'user', label: 'User', icon: User, color: 'bg-slate-100 text-slate-800' },
  { value: 'event_admin', label: 'Event Admin', icon: UserCog, color: 'bg-indigo-100 text-indigo-800' }
];

export default function UsersPage() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user',
    mobile: '',
    venues: ['all'],
    is_active: true
  });

  useEffect(() => {
    fetchUsers();
    fetchCurrentUser();
  }, []);

  const fetchCurrentUser = async () => {
    try {
      const response = await axios.get(`${API}/auth/me`);
      setCurrentUser(response.data);
    } catch (error) {
      console.error('Failed to fetch current user:', error);
    }
  };

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
      if (error.response?.status === 403) {
        toast.error('You do not have permission to view users');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const openCreateDialog = () => {
    setEditingUser(null);
    setFormData({
      name: '',
      email: '',
      password: '',
      role: 'user',
      mobile: '',
      venues: ['all'],
      is_active: true
    });
    setIsDialogOpen(true);
  };

  const openEditDialog = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      email: user.email,
      password: '',
      role: user.role || 'user',
      mobile: user.mobile || '',
      venues: user.venues || ['all'],
      is_active: user.is_active !== false
    });
    setIsDialogOpen(true);
  };

  const handleVenueToggle = (venue) => {
    if (venue === 'all') {
      setFormData({ ...formData, venues: ['all'] });
    } else {
      let newVenues = formData.venues.filter(v => v !== 'all');
      if (newVenues.includes(venue)) {
        newVenues = newVenues.filter(v => v !== venue);
      } else {
        newVenues.push(venue);
      }
      if (newVenues.length === 0) {
        newVenues = ['all'];
      }
      setFormData({ ...formData, venues: newVenues });
    }
  };

  const handleSave = async () => {
    if (!formData.name || !formData.email) {
      toast.error('Please fill in name and email');
      return;
    }
    if (!editingUser && !formData.password) {
      toast.error('Password is required for new users');
      return;
    }

    setSaving(true);
    try {
      if (editingUser) {
        const updateData = { ...formData };
        if (!updateData.password) {
          delete updateData.password;
        }
        await axios.put(`${API}/users/${editingUser.id}`, updateData);
        toast.success('User updated successfully');
      } else {
        await axios.post(`${API}/users`, formData);
        toast.success('User created successfully');
      }
      setIsDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Failed to save user:', error);
      toast.error(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user) => {
    if (!window.confirm(`Are you sure you want to delete ${user.name}?`)) {
      return;
    }
    try {
      await axios.delete(`${API}/users/${user.id}`);
      toast.success('User deleted successfully');
      fetchUsers();
    } catch (error) {
      console.error('Failed to delete user:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const getRoleConfig = (role) => {
    return ROLES.find(r => r.value === role) || ROLES[3];
  };

  const fmtLastLogin = (iso) => {
    if (!iso) return '—';
    try {
      return new Date(iso).toLocaleString('en-IN', { timeZone: 'Asia/Kolkata', day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit', hour12: true });
    } catch { return '—'; }
  };

  const filteredUsers = users.filter((u) => {
    if (roleFilter !== 'all' && (u.role || 'user') !== roleFilter) return false;
    if (statusFilter === 'active' && u.is_active === false) return false;
    if (statusFilter === 'inactive' && u.is_active !== false) return false;
    return true;
  });

  const isAllVenues = (venues) => {
    return !venues || venues.length === 0 || venues.includes('all');
  };

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="users-page">
        {/* Header */}
        <div className="text-center">
          <h1 className="font-heading font-bold text-3xl text-slate-900 tracking-tight">
            User Management
          </h1>
          <p className="text-slate-600 mt-1">Manage users and their venue access</p>
        </div>
        <div className="flex justify-center">
          <Button onClick={openCreateDialog} className="gap-2" data-testid="add-user-btn">
            <Plus className="w-4 h-4" />
            Add User
          </Button>
        </div>

        {/* Filters */}
        <Card className="p-4">
          <div className="flex flex-wrap gap-3 items-end">
            <div className="min-w-[180px]">
              <Label className="text-xs text-slate-500">Role</Label>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="mt-1" data-testid="users-role-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  {ROLES.map((r) => (<SelectItem key={r.value} value={r.value}>{r.label}</SelectItem>))}
                </SelectContent>
              </Select>
            </div>
            <div className="min-w-[180px]">
              <Label className="text-xs text-slate-500">Status</Label>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="mt-1" data-testid="users-status-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
        </Card>

        {/* Users List */}
        <Card className="p-6">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : users.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No users found. Create your first user.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Name</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Email</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Role</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Venues</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Status</th>
                    <th className="text-left py-3 px-4 font-semibold text-slate-700">Last Logged</th>
                    <th className="text-right py-3 px-4 font-semibold text-slate-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user) => {
                    const roleConfig = getRoleConfig(user.role);
                    const RoleIcon = roleConfig.icon;
                    const isPrimaryAdmin = user.email === 'admin@maks.com';
                    const isCurrentUser = currentUser?.id === user.id;
                    
                    return (
                      <tr key={user.id} className="border-b border-slate-100 hover:bg-slate-50">
                        <td className="py-3 px-4">
                          <div className="flex items-center gap-2">
                            <span className="font-medium text-slate-900">{user.name}</span>
                            {isPrimaryAdmin && (
                              <Badge variant="outline" className="text-xs bg-amber-50 border-amber-200 text-amber-700">
                                Primary
                              </Badge>
                            )}
                            {isCurrentUser && (
                              <Badge variant="outline" className="text-xs bg-blue-50 border-blue-200 text-blue-700">
                                You
                              </Badge>
                            )}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-slate-600">{user.email}</td>
                        <td className="py-3 px-4">
                          <Badge className={`${roleConfig.color} gap-1`}>
                            <RoleIcon className="w-3 h-3" />
                            {roleConfig.label}
                          </Badge>
                        </td>
                        <td className="py-3 px-4">
                          {user.role === 'event_admin' ? (
                            <Badge variant="outline" className="bg-indigo-50 border-indigo-200 text-indigo-700" data-testid={`user-assigned-events-${user.id}`}>
                              Assigned events only
                            </Badge>
                          ) : isAllVenues(user.venues) ? (
                            <Badge variant="outline" className="bg-green-50 border-green-200 text-green-700">
                              <Building2 className="w-3 h-3 mr-1" />
                              All Venues
                            </Badge>
                          ) : (
                            <div className="flex flex-wrap gap-1">
                              {user.venues.map((venue) => (
                                <Badge key={venue} variant="outline" className="text-xs">
                                  {venue}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </td>
                        <td className="py-3 px-4">
                          {user.is_active !== false ? (
                            <Badge className="bg-green-100 text-green-800 gap-1">
                              <CheckCircle className="w-3 h-3" />
                              Active
                            </Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-800 gap-1">
                              <XCircle className="w-3 h-3" />
                              Inactive
                            </Badge>
                          )}
                        </td>
                        <td className="py-3 px-4 text-slate-600 text-sm whitespace-nowrap" data-testid={`user-last-login-${user.id}`}>
                          {fmtLastLogin(user.last_login_at)}
                        </td>
                        <td className="py-3 px-4 text-right">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openEditDialog(user)}
                              data-testid={`edit-user-${user.id}`}
                            >
                              <Pencil className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => handleDelete(user)}
                              disabled={isPrimaryAdmin || isCurrentUser}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </Card>

        {/* Create/Edit Dialog */}
        <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <DialogContent className="max-w-lg">
            <DialogHeader>
              <DialogTitle>
                {editingUser ? 'Edit User' : 'Create New User'}
              </DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Name <span className="text-red-500">*</span></Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Enter name"
                    data-testid="user-name-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Email <span className="text-red-500">*</span></Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    placeholder="Enter email"
                    data-testid="user-email-input"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label>Mobile Number <span className="text-slate-400 text-xs">(used for expense approval WhatsApp)</span></Label>
                <Input
                  value={formData.mobile}
                  onChange={(e) => setFormData({ ...formData, mobile: e.target.value })}
                  placeholder="10-digit mobile e.g. 9876543210"
                  data-testid="user-mobile-input"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>
                    Password {!editingUser && <span className="text-red-500">*</span>}
                    {editingUser && <span className="text-slate-400 text-xs">(leave blank to keep)</span>}
                  </Label>
                  <Input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder={editingUser ? "••••••••" : "Enter password"}
                    data-testid="user-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Role <span className="text-red-500">*</span></Label>
                  <Select
                    value={formData.role}
                    onValueChange={(value) => setFormData({ ...formData, role: value })}
                    disabled={editingUser?.email === 'admin@maks.com'}
                  >
                    <SelectTrigger data-testid="user-role-select">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {ROLES.map((role) => (
                        <SelectItem key={role.value} value={role.value}>
                          <div className="flex items-center gap-2">
                            <role.icon className="w-4 h-4" />
                            {role.label}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Venue Access</Label>
                <div className="border rounded-lg p-4 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="all-venues"
                      checked={formData.venues.includes('all')}
                      onCheckedChange={() => handleVenueToggle('all')}
                    />
                    <label htmlFor="all-venues" className="text-sm font-medium text-slate-700 cursor-pointer">
                      All Venues (Full Access)
                    </label>
                  </div>
                  <div className="border-t pt-3">
                    <p className="text-xs text-slate-500 mb-2">Or select specific venues:</p>
                    <div className="grid grid-cols-1 gap-2">
                      {VENUES.map((venue) => (
                        <div key={venue} className="flex items-center space-x-2">
                          <Checkbox
                            id={`venue-${venue}`}
                            checked={!formData.venues.includes('all') && formData.venues.includes(venue)}
                            onCheckedChange={() => handleVenueToggle(venue)}
                          />
                          <label 
                            htmlFor={`venue-${venue}`} 
                            className="text-sm cursor-pointer text-slate-700"
                          >
                            {venue}
                          </label>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {editingUser && (
                <div className="flex items-center space-x-2 pt-2">
                  <Checkbox
                    id="is-active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                    disabled={editingUser?.id === currentUser?.id}
                  />
                  <label htmlFor="is-active" className="text-sm font-medium text-slate-700 cursor-pointer">
                    Account Active
                  </label>
                  {editingUser?.id === currentUser?.id && (
                    <span className="text-xs text-slate-400">(Cannot deactivate your own account)</span>
                  )}
                </div>
              )}
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? 'Saving...' : editingUser ? 'Update User' : 'Create User'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
